import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";
import { getCached, getStale, setCached } from "../utils/queryCache";
import { SEASON_YEAR } from "../utils/season";
import { ownershipPercentages } from "../utils/topTeams";

// Rosters only change when free agency bids resolve (6am Tuesday) or an
// instant add lands, so ownership is close to a weekly number. Six hours
// rather than a day so that a launch between the 4am week advance and the 6am
// bid run does not pin the pre-bid figure for the rest of Tuesday.
const OWNERSHIP_TTL_MS = 6 * 60 * 60 * 1000;

// Share of leagues rostering each team in `week`, as { slug: percent }.
// Cross-league, so it goes through get_team_ownership() (migration 063), a
// SECURITY DEFINER function, like useWeeklyAwards.
//
// Keyed by week and persisted: a cold launch paints the last known figures
// straight away, and the first launch after a week advance fetches fresh.
export function useTeamOwnership(week) {
  const key = `ownership:v2:${SEASON_YEAR}:w${week}`;
  const [ownership, setOwnership] = useState(() => (week ? getCached(key) || getStale(key) : null));

  useEffect(() => {
    if (!week) return undefined;
    const cached = getCached(key);
    if (cached) {
      setOwnership(cached);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_team_ownership", { p_week: week });
      if (cancelled || error || !data) return;
      const map = ownershipPercentages(data);
      setCached(key, map, OWNERSHIP_TTL_MS, { persist: true });
      setOwnership(map);
    })();
    return () => { cancelled = true; };
  }, [key, week]);

  return ownership;
}
